import React from "react";
import { motion } from "framer-motion";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import { SectionOrnament } from "@/components/SectionOrnament";

const STEPS = [
  {
    n: "01",
    label: "Observe",
    text: "Listen to the patterns beneath the noise. Witness what is, before reaching for what could be.",
  },
  {
    n: "02",
    label: "Reflect",
    text: "Sit with the stories, personal, cultural, structural, and let questions take shape.",
  },
  {
    n: "03",
    label: "Connect",
    text: "Bring people, ideas and disciplines into the same room. The thread is always relational.",
  },
  {
    n: "04",
    label: "Design",
    text: "Translate insight into structure. Frameworks, spaces, experiences that hold meaning.",
  },
  {
    n: "05",
    label: "Evolve",
    text: "Nothing is fixed. Every iteration is an invitation to deepen, refine and re,imagine.",
  },
];

export const HowItFlows = () => {
  return (
    <section
      id="flow"
      data-testid="flow-section"
      className="relative bg-bone py-28 md:py-44 overflow-hidden"
    >
      <SectionOrnament side="right" vertical="top-1/4" size={500} opacity={0.08} />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.1)}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-20"
        >
          <div>
            <motion.p variants={fadeUp} className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-4">
              ✦ &nbsp; 02 , How it flows
            </motion.p>
            <motion.h2
              variants={fadeUp}
              data-testid="flow-title"
              className="font-serif text-5xl md:text-7xl text-charcoal leading-[0.95] tracking-tight"
            >
              A living
              <br />
              <span className="italic text-bronze">rhythm.</span>
            </motion.h2>
          </div>
          <motion.p variants={fadeUp} className="max-w-md text-ink text-base md:text-lg font-light leading-relaxed">
            The same five movements show up at the scale of self, project,
            community and systems.{" "}
            <span className="italic font-serif text-bronze text-[1.3em]">
              Everything is interconnected.
            </span>
          </motion.p>
        </motion.div>

        {/* horizontal connecting line */}
        <div className="relative">
          <div className="hidden md:block absolute top-[42px] left-0 right-0 h-px bg-stone/40" />
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={stagger(0.14)}
            className="grid grid-cols-2 md:grid-cols-5 gap-y-14 gap-x-6"
          >
            {STEPS.map((s, i) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                data-testid={`flow-step-${s.label.toLowerCase()}`}
                className="relative group"
              >
                {/* node */}
                <div className="relative flex items-center justify-center md:justify-start">
                  <div className="w-[84px] h-[84px] rounded-full bg-sand border border-stone/40 flex items-center justify-center transition-all duration-700 group-hover:bg-forest group-hover:border-forest">
                    <span className="font-serif italic text-2xl text-bronze group-hover:text-bone transition-colors duration-700">
                      {s.n}
                    </span>
                  </div>
                </div>
                <h3 className="mt-6 font-serif text-2xl md:text-3xl text-charcoal text-center md:text-left">
                  {s.label}
                </h3>
                <p className="mt-3 text-sm text-ink/90 leading-relaxed text-center md:text-left max-w-[220px] mx-auto md:mx-0">
                  {s.text}
                </p>
                {/* arrow between */}
                {i < STEPS.length - 1 && (
                  <span className="hidden md:block absolute top-9 -right-3 text-bronze opacity-60">
                    →
                  </span>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HowItFlows;
